import {MDCCircularProgress} from '@material/circular-progress';

import {OBJ} from '../../obj';
import {ElObj, elObjOpts, ElObjOpts} from '../../elobj';
import {iterableToArray, pixelString, stringIterableToStringArray} from '../../util';

interface SVGElObjOpts extends Partial<ElObjOpts> {
	viewBox?: string;
}

@OBJ
class SVGElObj extends ElObj {
	constructor(opts: SVGElObjOpts) {
		const attributes = opts.attributes ?
			iterableToArray(opts.attributes) :
			[];
		if (opts.viewBox) {
			attributes.push(['viewBox', opts.viewBox]);
		}
		opts.attributes = attributes;
		opts.namespace = 'http://www.w3.org/2000/svg';
		if (!opts.tagName) {
			opts.tagName = 'svg';
		}
		super(opts);
	}
}

interface SVGCircleElObjMinAttrs {
	cx: string;
	cy: string;
	r: string;
	strokeWidth: string;
}

interface SVGCircleElObjAttrs extends SVGCircleElObjMinAttrs {
	strokeDashArray: string;
	strokeDashOffset: string;
}

interface SVGCircleElObjOpts extends Partial<ElObjOpts>, Partial<SVGCircleElObjAttrs> {
}

@OBJ
class SVGCircleElObj extends SVGElObj {
	constructor(opts: SVGCircleElObjOpts) {
		const attributes = opts.attributes ?
			iterableToArray(opts.attributes) :
			[];
		if (opts.cx) {
			attributes.push(['cx', opts.cx]);
		}
		if (opts.cy) {
			attributes.push(['cy', opts.cy]);
		}
		if (opts.r) {
			attributes.push(['r', opts.r]);
		}
		if (opts.strokeDashArray) {
			attributes.push(['stroke-dasharray', opts.strokeDashArray]);
		}
		if (opts.strokeDashOffset) {
			attributes.push(['stroke-dashoffset', opts.strokeDashOffset]);
		}
		if (opts.strokeWidth) {
			attributes.push(['stroke-width', opts.strokeWidth]);
		}
		opts.attributes = attributes;
		opts.tagName = 'circle';
		super(opts);
	}
}

interface DeterminateCircleGraphicOpts extends SVGElObjOpts {
	circle: SVGCircleElObjOpts;
	track: SVGCircleElObjOpts;
	viewBox: string;
}

@OBJ
class DeterminateCircleGraphic extends SVGElObj {
	constructor(opts: DeterminateCircleGraphicOpts) {
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-circular-progress__determinate-circle-graphic',
			...classNames,
		];
		super(opts);
		new SVGCircleElObj({
			classNames: [
				'mdc-circular-progress__determinate-track',
			],
			parent: this,
			...(opts.track || {}),
		});
		new SVGCircleElObj({
			classNames: [
				'mdc-circular-progress__determinate-circle',
			],
			parent: this,
			...(opts.circle || {}),
		});
	}
}

interface DeterminateContainerOpts extends Partial<ElObjOpts> {
	circleGraphic: {
		circle: SVGCircleElObjOpts;
		track: SVGCircleElObjOpts;
		viewBox: string;
	}
}

@OBJ
class DeterminateContainer extends ElObj {
	constructor(opts: DeterminateContainerOpts) {
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-circular-progress__determinate-container',
			...classNames,
		];
		opts.tagName = 'div';
		super(opts);
		new DeterminateCircleGraphic({
			...(opts.circleGraphic || {}),
			parent: this,
		});
	}
}

interface IndeterminateCircleGraphicOpts extends SVGElObjOpts {
	circle: SVGCircleElObjOpts;
	viewBox: string;
}

@OBJ
class IndeterminateCircleGraphic extends SVGElObj {
	constructor(opts: IndeterminateCircleGraphicOpts) {
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-circular-progress__indeterminate-circle-graphic',
			...classNames,
		];
		super(opts);
		new SVGCircleElObj({
			parent: this,
			...(opts.circle || {}),
		});
	}
}

interface IndeterminateContainerOpts extends Partial<ElObjOpts> {
	circleLeft: IndeterminateCircleGraphicOpts;
	gapPatch: IndeterminateCircleGraphicOpts;
	circleRight: IndeterminateCircleGraphicOpts;
}

@OBJ
class IndeterminateContainer extends ElObj {
	constructor(opts: IndeterminateContainerOpts) {
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-circular-progress__indeterminate-container',
			...classNames,
		];
		opts.tagName = 'div';
		super(opts);
		const spinnerLayer = new ElObj({
			classNames: [
				'mdc-circular-progress__spinner-layer',
			],
			parent: this,
			tagName: 'div',
		});
		const circleLeft = new ElObj({
			classNames: [
				'mdc-circular-progress__circle-clipper',
				'mdc-circular-progress__circle-left',
			],
			parent: spinnerLayer,
			tagName: 'div',
		});
		new IndeterminateCircleGraphic({
			...(opts.circleLeft),
			parent: circleLeft,
		});
		const gapPatch = new ElObj({
			classNames: [
				'mdc-circular-progress__gap-patch',
			],
			parent: spinnerLayer,
			tagName: 'div',
		});
		new IndeterminateCircleGraphic({
			...(opts.gapPatch),
			parent: gapPatch,
		});
		const circleRight = new ElObj({
			classNames: [
				'mdc-circular-progress__circle-clipper',
				'mdc-circular-progress__circle-right',
			],
			parent: spinnerLayer,
			tagName: 'div',
		});
		new IndeterminateCircleGraphic({
			...(opts.circleRight),
			parent: circleRight,
		});
	}
}

type CircularProgressSize = 'small' | 'medium' | 'large';

interface CircularProgressOpts extends ElObjOpts {
	indeterminate: boolean;
	progress: number;
	size: CircularProgressSize;
}

@OBJ
export class CircularProgress extends ElObj {
	private ctrl: MDCCircularProgress;

	constructor(opts: Partial<CircularProgressOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<CircularProgressOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<CircularProgressOpts> | null, tagName?: TagName);
	constructor(opts: Partial<CircularProgressOpts> | null, root?: Element | null);
	constructor(opts: Partial<CircularProgressOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<CircularProgressOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<CircularProgressOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<CircularProgressOpts>(a, b, c);
		const attributes = opts.attributes ?
			iterableToArray(opts.attributes) :
			[];
		opts.attributes = [
			['aria-label', 'progress bar'],
			['aria-valuemin', '0'],
			['aria-valuemax', '1'],
			['role', 'progressbar'],
			...attributes,
		];
		const classNames = opts.classNames ?
			stringIterableToStringArray(opts.classNames) :
			[];
		opts.classNames = [
			'mdc-circular-progress',
			...classNames,
		];
		const size: CircularProgressSize = opts.size || 'small';
		let cx: string;
		let cy: string;
		let determinateStrokeDashOffset: string;
		let gapPatchStrokeWidth: string;
		let height: number;
		let r: string;
		let strokeDashArray: string;
		let strokeDashOffset: string;
		let strokeWidth: string;
		let viewBox: string;
		let width: number;
		switch (size) {
			case 'small':
				cx = '12';
				cy = '12';
				determinateStrokeDashOffset = '54.978';
				gapPatchStrokeWidth = '2';
				height = 24;
				r = '8.75';
				strokeDashArray = '54.978';
				strokeDashOffset = '27.489';
				strokeWidth = '2.5';
				viewBox = '0 0 24 24';
				width = 24;
				break;
			case 'medium':
				cx = '16';
				cy = '16';
				determinateStrokeDashOffset = '78.54';
				gapPatchStrokeWidth = '2.4';
				height = 36;
				r = '12.5';
				strokeDashArray = '78.54';
				strokeDashOffset = '39.27';
				strokeWidth = '3';
				viewBox = '0 0 32 32';
				width = 36;
				break;
			case 'large':
				cx = '24';
				cy = '24';
				determinateStrokeDashOffset = '113.097';
				gapPatchStrokeWidth = '3.2';
				height = 48;
				r = '18';
				strokeDashArray = '113.097';
				strokeDashOffset = '56.549';
				strokeWidth = '4';
				viewBox = '0 0 48 48';
				width = 48;
				break;
		}
		const styles = opts.styles ?
			iterableToArray(opts.styles) :
			[];
		opts.styles = [
			['height', pixelString(height)],
			['width', pixelString(width)],
			...styles,
		];
		opts.tagName = 'div';
		super(opts);
		new DeterminateContainer({
			circleGraphic: {
				circle: {
					cx,
					cy,
					r,
					strokeDashArray,
					strokeDashOffset: determinateStrokeDashOffset,
					strokeWidth,
				},
				track: {
					cx,
					cy,
					r,
					strokeWidth,
				},
				viewBox,
			},
			parent: this,
		});
		new IndeterminateContainer({
			circleLeft: {
				circle: {
					cx,
					cy,
					r,
					strokeDashArray,
					strokeDashOffset,
					strokeWidth,
				},
				viewBox,
			},
			gapPatch: {
				circle: {
					cx,
					cy,
					r,
					strokeDashArray,
					strokeDashOffset,
					strokeWidth: gapPatchStrokeWidth,
				},
				viewBox,
			},
			circleRight: {
				circle: {
					cx,
					cy,
					r,
					strokeDashArray,
					strokeDashOffset,
					strokeWidth,
				},
				viewBox,
			},
			parent: this,
		});
		this.ctrl = new MDCCircularProgress(this.elem);
		if (opts.indeterminate !== undefined) {
			this.setIndeterminate(opts.indeterminate);
		}
		if (opts.progress !== undefined) {
			this.setProgress(opts.progress);
		}
	}

	close(): void {
		this.setOpen(false);
	}

	destroy(): void {
		this.close();
		this.ctrl.destroy();
		super.destroy();
	}

	hide(): void {
		this.setVisible(false);
	}

	isHidden(): boolean {
		return !this.isVisible();
	}

	isOpen(): boolean {
		return !this.ctrl.isClosed;
	}

	isVisible(): boolean {
		return this.isOpen();
	}

	open(): void {
		this.setOpen(true);
	}

	setIndeterminate(indeterminate: boolean): void {
		this.ctrl.determinate = !indeterminate;
	}

	setOpen(open: boolean): void {
		if (open === this.isOpen()) {
			return;
		}
		if (open) {
			this.ctrl.open();
		} else {
			this.ctrl.close();
		}
	}

	setProgress(aNumberBetweenZeroAndOne: number): void {
		this.ctrl.progress = aNumberBetweenZeroAndOne;
	}

	setVisible(visible: boolean): void {
		this.setOpen(visible);
	}

	show(): void {
		this.setVisible(true);
	}
}
